import { useMemo, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { Permission } from '../../api/types';
import { usePermissionsList } from '../../api/queries';

/**
 * Read-only catalogue browser. The permission list is seed-defined; edits
 * happen via release migrations, not the portal.
 */
export default function PermissionsPage() {
  usePageTitle('Permissions');
  const list = usePermissionsList();
  const [detailRow, setDetailRow] = useState<Permission | null>(null);

  const columns = useMemo<GridColDef<Permission>[]>(() => [
    {
      field: 'code', headerName: 'Code', flex: 1.2,
      renderCell: (p) => (
        <Typography component="code" sx={{
          fontFamily: 'monospace', fontSize: 12,
          bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 0.5,
        }}>{p.row.code}</Typography>
      ),
    },
    { field: 'displayName', headerName: 'Name', flex: 1.5 },
    { field: 'category',    headerName: 'Category', width: 160,
      renderCell: (p) => <Chip size="small" variant="outlined" label={p.row.category} /> },
    // #161 — render the full tier set as a row of chips. A permission
    // tagged [Operator, Platform] shows two chips.
    { field: 'tiers',       headerName: 'Tiers', width: 200, sortable: false,
      renderCell: (p) => <TierChips tiers={p.row.tiers} /> },
    { field: 'description', headerName: 'Description', flex: 2 },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'code',        label: 'Code' },
    { kind: 'text', key: 'displayName', label: 'Name' },
    { kind: 'text', key: 'description', label: 'Description' },
  ], []);

  // Quick filters by tier-set membership.
  const quickFilters = useMemo<QuickFilter<Permission>[]>(() => [
    { label: 'All',      filter: () => true },
    { label: 'Platform', filter: (r) => r.tiers?.includes('Platform') ?? false },
    { label: 'Operator', filter: (r) => r.tiers?.includes('Operator') ?? false },
    { label: 'Supplier', filter: (r) => r.tiers?.includes('Supplier') ?? false },
    { label: 'Multi-tier', filter: (r) => (r.tiers?.length ?? 0) > 1 },
  ], []);

  const exportColumns = useMemo<ExportColumn<Permission>[]>(() => [
    { key: 'code',        label: 'Code' },
    { key: 'displayName', label: 'Name' },
    { key: 'category',    label: 'Category' },
    { key: 'tiers',       label: 'Tiers', format: (v) => Array.isArray(v) ? v.join(', ') : '' },
    { key: 'description', label: 'Description', format: (v) => fmtOrEmDash(v) },
  ], []);

  const actions = useMemo<RowAction<Permission>[]>(() => [
    { label: 'View', onClick: (row) => setDetailRow(row) },
  ], []);

  const detailFields = useMemo<DetailRow<Permission>[]>(() => [
    { label: 'Code',        render: (p) => <code>{p.code}</code> },
    { label: 'Name',        render: (p) => p.displayName },
    { label: 'Category',    render: (p) => p.category },
    { label: 'Tiers',       render: (p) => <TierChips tiers={p.tiers} /> },
    { label: 'Description', render: (p) => p.description ?? '—' },
  ], []);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Permission catalogue</Typography>
        <Typography variant="body2" color="text.secondary">
          Read-only — defined in code and applied by release migrations.
          Operator admins clone a system role and select which catalogue
          entries to grant in their custom role; the catalogue itself is
          immutable from the portal.
        </Typography>

        <RouteDataGrid<Permission>
          entityName="Permissions"
          queryKey={list.queryKey}
          listFn={list.listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Permission catalogue"
          actions={actions}
          onRowClick={(row) => setDetailRow(row)}
        />

        <EntityDetailDialog<Permission>
          open={detailRow !== null}
          title={detailRow?.displayName ?? 'Permission'}
          data={detailRow}
          fields={detailFields}
          onClose={() => setDetailRow(null)}
        />
      </Stack>
    </>
  );
}

function TierChip({ tier }: { tier: string }) {
  const color = tier === 'Platform' ? 'secondary'
              : tier === 'Operator' ? 'primary'
              : 'default';
  return <Chip size="small" variant="outlined" label={tier} color={color as 'secondary' | 'primary' | 'default'} />;
}

// #161 — render the tier set as inline chips. A permission can belong to
// multiple tiers (e.g. apps.technicians.use is [Operator, Platform]).
function TierChips({ tiers }: { tiers: string[] }) {
  if (!tiers || tiers.length === 0) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5}>
      {tiers.map((t) => <TierChip key={t} tier={t} />)}
    </Stack>
  );
}
